import { Box, Button, Card, CardContent, Checkbox, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { cloneDeep, remove } from "lodash";
import React, { useContext, useEffect, useState } from "react";
import { PermissionContext } from "../../contexts/PermissionContext";
import { IMaterial, IMaterialInformationFormSection, MaterialContext } from "../../interfaces";
import { Role, Sort } from "../../utils";
import { FormSectionHeader } from "../Shared/FormSectionHeader";
import { MaterialInputDialog } from "../MaterialInputDialog";
import { DeleteMaterialDialog } from "./DeleteMaterialDialog";
import { DiscrepancyDialog } from "./DiscrepancyDialog";
import { MaterialApprovedTableRow } from "./MaterialApprovedTableRow";
import { MaterialUndeliveredTableRow } from "./MaterialUndeliveredTableRow";
import { MaterialTableRow } from "./MaterialTableRow";
import { LocationDialog } from "./LocationDialog";
import { HistoricMaterialInformationAlert } from "../Shared/HistoricMaterialInformationAlert";

export const MaterialInformationForm: React.FC<IMaterialInformationFormSection> = ({
    entityId,
    status,
    materials,
    onUpdateMaterials,
    staticMaterials,
    onUpdateMaterial,
    onImportComplete,
    readonly,
    readonlyMaterialActions,
    saving,
    ...props
}: IMaterialInformationFormSection) => {



    const [deleteDialog, setDeleteDialog] = useState(false);
    const [discrepancyDialog, setDiscrepancyDialog] = useState(false);
    const [locationDialog, setLocationDialog] = useState(false);

    const [selectedMaterial, setSelectedMaterial] = useState<IMaterial | null>(null);
    const [selectedMaterials, setSelectedMaterials] = useState<IMaterial[]>([]);

    const permissionContext = useContext(PermissionContext);

    const getMaterials = (undelivered?: boolean) => {
        //deep copy the object

        undelivered = undelivered === undefined ? false : undelivered;

        let clone = cloneDeep(materials)

        if (undelivered === true)
            clone = clone.filter(x => x.undelivered === true)
        else clone = clone.filter(x => x.undelivered !== true)


        //order materials alphabetically
        Sort.materials(clone);

        return clone;

    }

    const addMaterial = (material: IMaterial) => {

        let model = getMaterials();
        model.push(material);
        onUpdateMaterials(model);
    }


    const copyMaterial = (material: IMaterial) => {
        let copy = cloneDeep(material);
        copy.materialId = undefined;
        addMaterial(copy);
    }

    const selectMaterial = (material: IMaterial, selected: boolean) => {
        if (selected === true)
            setSelectedMaterials((prev) => {
                return [material, ...prev];
            });
        else setSelectedMaterials((prev) => {
            return prev.filter(x => x.materialId !== material.materialId);
        });
    }

    const removeMaterial = (material: IMaterial) => {

        let model: IMaterial[] = getMaterials().filter(x =>
            JSON.stringify(x) !== JSON.stringify(material)
        );

        onUpdateMaterials(model);
    }

    const onRemove = (material: IMaterial) => {
        setSelectedMaterial(material);
        setDeleteDialog(true);
    }

    const onDiscrepancy = (material: IMaterial) => {
        setSelectedMaterial(material);
        setDiscrepancyDialog(true);
    }

    const onSelectAll = (checked: boolean) => {
        if (!checked) setSelectedMaterials([]);
        else setSelectedMaterials(materials);
    }

    const handleCloseDeleteDialog = (confirm: boolean) => {
        setDeleteDialog(false);

        if (confirm && selectedMaterial)
            removeMaterial(selectedMaterial);

        setSelectedMaterial(null);
    }

    const handleCloseDiscrepancyDialog = (discrepancy?: string, remove?: boolean) => {
        setDiscrepancyDialog(false);

        //dont update anything if readonly
        if (readonlyMaterialActions)
            return;

        if (selectedMaterial) {

            let updatedMaterial = cloneDeep(selectedMaterial);

            updatedMaterial.discrepancy = discrepancy;

            if (remove === true)
                updatedMaterial.undelivered = true;


            onUpdateMaterial(updatedMaterial);
        }

        setSelectedMaterial(null);
    }

    const canAmend = () => {

        //it should only be possible to add or remove materials
        //from the table during initial submission or resubmission

        return (status === 1 || status === 4);
    }

    const showApprovedMaterialRows = () => {

        //it should only be possible to add more detail
        //once the request is approved and accessed by I-AP

        return ([3, 5, 6].includes(status) && permissionContext.isRole(Role.Internal.AssetProtection));
    }

    const showMultiSelect = () => {
        //it should only be possible to update multiple materials
        //while the approved request is open and accessed by I-AP
        return ([3, 5].includes(status) && permissionContext.isRole(Role.Internal.AssetProtection));

    }

    useEffect(() => {
    }, [materials, selectedMaterials])

    return (
        <Card>
            <FormSectionHeader label="Materials" saving={saving} />
            <CardContent>
                <Grid container>
                    {staticMaterials ? <Box><HistoricMaterialInformationAlert /></Box>  : null}
                    {materials.length > 0 ?
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Description</TableCell>
                                        <TableCell>Vehicle Class</TableCell>
                                        <TableCell>Type</TableCell>
                                        <TableCell>Part Identification</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Qty</TableCell>
                                        <TableCell />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {getMaterials().map((material, i) => showApprovedMaterialRows() ?

                                        <MaterialApprovedTableRow
                                            material={material}
                                            onAddDiscrepancy={() => { onDiscrepancy(material) }}
                                            onUpdate={onUpdateMaterial}
                                            onSelect={selectMaterial}
                                            selectedMaterials={selectedMaterials}
                                            onCopy={() => copyMaterial(material)}
                                            status={status}
                                        />

                                        :

                                        <MaterialTableRow
                                            key={i}
                                            material={material}
                                            onUpdate={onUpdateMaterial}
                                            onCopy={() => copyMaterial(material)}
                                            onRemove={() => removeMaterial(material)}
                                            readonly={!canAmend()}
                                        />

                                    )}
                                    {getMaterials(true).map((material, i) => MaterialUndeliveredTableRow(
                                        material,
                                        () => { onDiscrepancy(material) }
                                    ))}
                                </TableBody>
                            </Table>
                            {showMultiSelect() &&
                                <Box>
                                    <Checkbox
                                        indeterminate={(selectedMaterials.length > 0 && selectedMaterials.length !== materials.length)}
                                        checked={selectedMaterials.length === materials.length}
                                        onChange={(_, checked) => { onSelectAll(checked) }}
                                    />

                                    {selectedMaterials.length === 0 ?
                                        <Button disabled>Select All</Button>
                                        :
                                        <Button variant="contained" onClick={() => setLocationDialog(true)}>Update Selected</Button>

                                    }

                                </Box>
                            }


                        </TableContainer>
                        :
                        <Typography
                            sx={{ pb: 2 }}
                            variant="subtitle1"
                        >
                            Populate the storage request by clicking on 'Add Material'.
                        </Typography>
                    }
                </Grid>

                <Grid container>
                    {canAmend() &&
                        <MaterialInputDialog
                            entity={{ id: entityId ?? "", type: "STORAGEREQUEST" }}
                            context={MaterialContext.MaterialTable}
                            onSubmitMaterial={(material) => addMaterial(material)}
                            onImportComplete={onImportComplete}
                            focus={materials.length === 0}
                        />
                    }
                </Grid>

                <DeleteMaterialDialog
                    open={deleteDialog}
                    handleClose={handleCloseDeleteDialog}
                />

                <DiscrepancyDialog
                    open={discrepancyDialog}
                    handleClose={handleCloseDiscrepancyDialog}
                    material={selectedMaterial as IMaterial}
                    readonly={readonlyMaterialActions}
                />

                <LocationDialog
                    open={locationDialog}
                    handleClose={() => setLocationDialog(false)}
                    materials={selectedMaterials}
                    onUpdate={onUpdateMaterial}
                />

            </CardContent>
        </Card>
    );
}

